import { faBullseyeArrow, faTag, faUsers } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import React from "react";
import { AudienceService } from "src/app/services/FanService";
import "./AdminAudiences.css";

type Props = {};
type State = {
	available: boolean;
	isLoading: boolean;
	audiences: any[];
	selected: any;
	generatedAt: string | null;
	snapshots: boolean;
	showDownload: boolean;
};

const GROUPS = [
	{ key: "Service", label: "SERVICE", icon: faBullseyeArrow },
	{ key: "Lifecycle", label: "LIFECYCLE", icon: faUsers },
	{ key: "Product", label: "PRODUCT", icon: faTag }
];

/**
 * Fan segments for targeting messages.
 *
 * Sizes are computed live rather than snapshotted, so the "last updated" here
 * is when this page loaded — it can't go stale between load and use. Once
 * audiences are materialised, this is where the refresh control and the
 * over-a-week-old block belong.
 */
export class AdminAudiences extends React.Component<Props, State> {
	toast: any;

	constructor(props: Props) {
		super(props);
		this.state = {
			available: true, isLoading: true, audiences: [], selected: null,
			generatedAt: null, snapshots: false, showDownload: false
		};
	}

	componentDidMount() {
		this.load();
	}

	async load() {
		this.setState({ isLoading: true });
		try {
			const res = await AudienceService.getAll();
			if (!res.ok || res.status === 204) {
				this.setState({ available: false, isLoading: false });
				return;
			}
			const body = await res.json();
			if (!body?.available) {
				this.setState({ available: false, isLoading: false });
				return;
			}
			this.setState({
				audiences: Array.isArray(body.audiences) ? body.audiences : [],
				generatedAt: body.generatedAt || null,
				snapshots: !!body.snapshots,
				isLoading: false
			});

			// Keep the panel on the same audience across a refresh.
			if (this.state.selected) {
				const again = (body.audiences || []).find((a: any) => a.key === this.state.selected.key);
				if (again) this.setState({ selected: again });
			}
		} catch {
			this.setState({ available: false, isLoading: false });
		}
	}

	download(optedInOnly: boolean) {
		const key = this.state.selected?.key;
		if (!key) return;
		window.open(AudienceService.downloadUrl(key, optedInOnly), "_blank");
		this.setState({ showDownload: false });
	}

	renderPanel() {
		const audience = this.state.selected;

		if (!audience) {
			return <div className="au-panel"><h3 className="au-panel-empty">Select an audience</h3></div>;
		}

		const updated = this.state.generatedAt ? new Date(this.state.generatedAt) : null;

		return (
			<div className="au-panel">
				<div className="au-panel-head">
					<h3>{audience.name}</h3>
					<span className="au-group-pill">{audience.group}</span>
				</div>
				<p className="au-panel-desc">{audience.description}</p>

				{!audience.available && <div className="au-unavailable">{audience.unavailable}</div>}

				{audience.available && <>
					<div className="au-updated">
						<span>
							Last updated: {updated ? `${updated.toLocaleDateString("en-GB")} ${updated.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}` : "—"}
						</span>
						<Button className="p-button-sm p-button-outlined" label="Refresh" onClick={() => this.load()} />
					</div>

					<div className="au-metric">
						<span className="au-metric-label">TOTAL SIZE</span>
						<span className="au-metric-value">{(audience.size ?? 0).toLocaleString("en-GB")}</span>
					</div>
					<div className="au-metric">
						<span className="au-metric-label">OPTED INTO MARKETING</span>
						<span className="au-metric-value">{(audience.optedIntoMarketing ?? 0).toLocaleString("en-GB")}</span>
					</div>

					<div className="au-actions">
						{/* Nothing to send with yet — campaigns aren't built. */}
						<Button className="au-send" disabled icon="pi pi-comment" label="Send push" title="Push campaigns aren't built yet" />
						<div className="au-download">
							<Button className="p-button-outlined" label="Download" onClick={() => this.setState({ showDownload: !this.state.showDownload })} />
							{this.state.showDownload && <div className="au-menu">
								<button onClick={() => this.download(false)} type="button">All fans</button>
								{/* Only these may lawfully be mailed. */}
								<button onClick={() => this.download(true)} type="button">Opted into marketing</button>
							</div>}
						</div>
					</div>
				</>}
			</div>
		);
	}

	render() {
		const { available, isLoading, audiences } = this.state;

		if (!isLoading && !available) {
			return (
				<div style={{ padding: 20 }}>
					<h2 style={{ marginBottom: 20 }}>Audiences</h2>
					<div className="au-empty">Audiences aren&rsquo;t available on this environment yet.</div>
				</div>
			);
		}

		return (
			<div style={{ padding: 20 }}>
				<Toast ref={(el) => (this.toast = el)} />
				<h2 style={{ marginBottom: 18 }}>Audiences</h2>

				{isLoading && <p style={{ color: "var(--pb-text-muted)" }}>Loading&hellip;</p>}

				{!isLoading && <div className="au-layout">
					<div className="au-list">
						{GROUPS.map((group) => {
							const mine = audiences.filter((a: any) => a.group === group.key);
							if (!mine.length) return null;
							return (
								<div key={`grp-${group.key}`}>
									<h5 className="au-group">{group.label}</h5>
									{mine.map((audience: any) => (
										<button
											className={`au-card${this.state.selected?.key === audience.key ? " active" : ""}${audience.available ? "" : " off"}`}
											key={audience.key}
											onClick={() => this.setState({ selected: audience, showDownload: false })}
											type="button"
										>
											<span className="au-card-icon"><FontAwesomeIcon icon={group.icon} /></span>
											<span className="au-card-body">
												<span className="au-card-name">{audience.name}</span>
												<span className="au-card-desc">{audience.description}</span>
											</span>
											{audience.available && <span className="au-card-size">{(audience.size ?? 0).toLocaleString("en-GB")}</span>}
										</button>
									))}
								</div>
							);
						})}
					</div>

					<div className="au-side">
						<h5 className="au-group">AUDIENCE INSIGHTS</h5>
						{this.renderPanel()}
					</div>
				</div>}
			</div>
		);
	}
}
